import React, { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import addBlockIcon from '@src/assets/icons/add-block.svg';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import apis from '@src/apis';
import {
  SIDEBAR_WIDTH_COLLAPSED,
  FLASH_SALE_BANNER_HEIGHT,
} from '@src/styles/config';
import {
  CONVERTING_STATUS,
  DIRECTION,
  MAX_CHARACTER_PER_BLOCK,
  HEADER_HEIGHT,
  AUDIO_PLAYER_HEIGHT,
} from '@src/constants/project';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import { useHistory } from 'react-router-dom';
import { updateProject } from '@src/apis/project';
import { countTextLength } from '@src/services/tts';
import {
  DEFAULT_BREAK_TIME,
  DEFAULT_SPEED,
  // OUTPUT_AUDIO,
} from '@src/constants/voice';
import actions from '@src/redux/actions';
import { ERROR_CODE } from '@src/errors/code';
import { useAudioPlayerMultiBlock } from '@src/hooks/useAudioPlayerMultiBlock';
import {
  FREE_PACKAGE_CODES,
  PACKAGE_LEVEL_V3,
  PACKAGE_STUDIO_VERSION,
} from '@src/constants/package';
import { checkCustomPackage } from '@src/services/package';
import OutOfCreditsCustom from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsCustom';
import OutOfCreditsVip from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsVip';
import OutOfCreditsFree from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsFree';
import useUserInfo from '@src/hooks/useTtsUser';
import { DIALOG_V2_TYPE } from '@src/constants';
import FreeMaxCharsLimitExceeded from '@src/containers/TTSNew/MaxCharsLimitExceeded/FreeMaxCharsLimitExceeded';
import PaidMaxCharsLimitExceeded from '@src/containers/TTSNew/MaxCharsLimitExceeded/PaidMaxCharsLimitExceeded';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import LockCycle from '@src/assets/images/lock-cycle.svg';
import ROUTES from '@src/constants/route';
import { INIT_NOTIFICATION_DIALOG } from '@src/constants/dialog';
import ExpiredPackageAlert from '@src/containers/TTSNew/ExpiredPackageAlert';
import OutOfCreditsStandard from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsStandard';
import OutOfCreditsSpecial from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsSpecial';
import useSynthesisValidation from '@src/hooks/useSynthesisValidation';
import NotificationDialog from '@src/components/NotificationDialog';
import {
  audioExpired,
  checkBlocksChanged,
  isBlockChanged,
  getBlockWithAudio,
} from '@src/services/project';
import AudioPlayer from './AudioPlayer';
import CustomActionLeavePage from './CustomActionLeavePage';
import { StyledActionWarning, StyledContent } from './index.style';
import VirtualizedContentList from './VirtualizedContentList';

const Content = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const addBlockRef = useRef({});
  const dispatch = useDispatch();
  const { openSidebar } = useSelector((state) => state.layout);
  const { headerVoucher } = useSelector((state) => state.voucher);

  const user = useSelector((state) => state.auth.user);
  const {
    multiBlock,
    projectId,
    title,
    voiceDefault,
    currentBlockId,
    isDownloading,
    isProjectSynthesizing,
    originalTitle,
    isPlayingBlock,
    currentPlayingBlockId,
    openDialogChangeFormat,
    audioType,
    originalAudioType,
  } = useSelector((state) => state.project);
  const { usingPackage } = useSelector((state) => state.user);
  const { ttsUser } = useUserInfo();

  const { blocks, blockIds, originalBlocks, originalBlockIds } = multiBlock;
  const isChangedTitle = originalTitle !== title;
  const isChangedAudioType = originalAudioType !== audioType;
  const hasBlockChanged = checkBlocksChanged({
    blocks,
    originalBlocks,
    blockIds,
    originalBlockIds,
  });
  const isDraft = hasBlockChanged || isChangedTitle || isChangedAudioType;
  const blockIdsRef = useRef(blockIds);
  const blocksRef = useRef(blocks);
  const [characterExceed, setCharacterExceed] = useState(false);
  const [focusingBlockId, setFocusingBlockId] = useState(null);
  const [openOutOfCreditsDialog, setOpenOutOfCreditsDialog] = useState(false);
  const [warningOneTimeCredits, setWarningOneTimeCredits] = useState(false);
  const [openWrongSyntaxDialogV2, setOpenWrongSyntaxDialogV2] = useState(null);
  const [charsFactorCheckbox, setCharsFactorCheckbox] = useState(false);
  const [openWrongSyntaxDialog, setOpenWrongSyntaxDialog] = useState(null);
  const [openExpiredPackageDialog, setOpenExpiredPackageDialog] =
    useState(false);
  const [openMaxCharsLimitExceeded, setOpenMaxCharsLimitExceeded] =
    useState(false);
  const [openDialogLockedCycle, setOpenDialogLockedCycle] = useState(null);
  const [queueSynthesis, setQueueSynthesis] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [scrollToBlockId, setScrollToBlockId] = useState(null);
  const [queueMultiBlock, setQueueMultiBlock] = useState([]);
  const [isSynthesisMultiBlock, setIsSynthesisMultiBlock] = useState(false);

  const packageCode = usingPackage?.code;
  const maxLengthInputText = ttsUser?.maxLengthInputText || 0;

  const { getFeatureValue } = useFeatureFlags();

  const {
    handlePlay,
    handlePause,
    handleSeeked,
    isLoading,
    isPlaying,
    currentTime,
    duration,
    handleChangeVolume,
    handlePlayAudioInBlock,
    handleLoadAudio,
  } = useAudioPlayerMultiBlock();

  const { validateSynthesis } = useSynthesisValidation();

  const [isOpenUnsavedChangesDialog, setIsOpenUnsavedChangesDialog] =
    useState(false);

  const useHeaderBanner = getFeatureValue(
    FEATURE_KEYS.HEADER_BANNER_FLASH_SALE,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const handleCloseExpiredPackageDialog = () => {
    setOpenExpiredPackageDialog(false);
  };
  const openLimitedFeatureDialog = (featureKey) => {
    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('limitedFeatureDesc', { feature: t(featureKey) }),
      }),
    );
  };
  const handleCloseMaxCharsLimitExceededDialog = () =>
    setOpenMaxCharsLimitExceeded(false);
  const isShowHeaderBanner =
    useHeaderBanner && headerVoucher && headerVoucher.length > 0;
  const blockWithText = Object.keys(blocks)
    .map((blockId) => blocks[blockId])
    .filter((block) => block?.text);
  const totalBlockWithTextAndStatus = blockWithText.length;
  const blockSuccessSynthesis = Object.values(blocks).filter(
    (block) => block.status === CONVERTING_STATUS.SUCCESS,
  );
  const totalBlockSuccessSynthesis = blockSuccessSynthesis.length;
  const synthesisProgress = Math.ceil(
    (totalBlockSuccessSynthesis * 100) / totalBlockWithTextAndStatus,
  );
  const isAudioLinkExist = useMemo(
    () =>
      blockWithText.every(
        (block) =>
          block?.audioLink && block?.status === CONVERTING_STATUS.SUCCESS,
      ),
    [blockWithText],
  );

  const blockWithTextChanged = useMemo(
    () =>
      blockWithText.filter((block) => {
        const withoutExtension = block?.audioLink?.split('.')?.pop();
        const currentBlock = {
          text: block?.text,
          voiceCode: block?.voiceCode,
          speed: block?.speed,
          audioType: withoutExtension,
        };
        const originalBlock = {
          text: originalBlocks[block.id]?.text,
          voiceCode: originalBlocks[block.id]?.voiceCode,
          speed: originalBlocks[block.id]?.speed,
          audioType,
        };
        return (
          isBlockChanged(currentBlock, originalBlock) ||
          !block?.audioLink ||
          audioExpired(block?.audioExpiredAt)
        );
      }),
    [blockWithText, originalBlocks, audioType],
  );

  const synthesisInProgress = blockWithTextChanged.filter(
    (block) => block.status === CONVERTING_STATUS.IN_PROGRESS,
  );

  const isDisableConvertBtn =
    synthesisInProgress?.length === blockWithTextChanged?.length;

  const hasAudioExpired = blockWithText.some((block) =>
    audioExpired(block?.audioExpiredAt),
  );
  const isSynthesisInProgress =
    blockWithText.filter(
      (block) => block.status === CONVERTING_STATUS.IN_PROGRESS,
    ).length > 0;
  const isDisabledBtnAudioEffects =
    !blockWithText?.length ||
    hasAudioExpired ||
    isSynthesisInProgress ||
    blockWithTextChanged?.length;

  const isMultiBlockProcessing = useMemo(
    () =>
      blockWithText.filter(
        (block) => block.status === CONVERTING_STATUS.IN_PROGRESS,
      ).length > 0,
    [blockWithText],
  );
  const isProjectConverting = isProjectSynthesizing && isMultiBlockProcessing;
  const handleCloseOutOfCreditsDialog = () => setOpenOutOfCreditsDialog(false);

  const handleCloseNotificationDialogV2 = () =>
    dispatch(
      actions.dialog.displayDialogV2({
        notificationDialogV2: INIT_NOTIFICATION_DIALOG,
      }),
    );

  const renderButtonUnderstand = () => (
    <Button
      variant="contained"
      onClick={handleCloseNotificationDialogV2}
      sx={{
        float: 'right',
        marginLeft: '-10px',
        marginBottom: '-10px',
        padding: '8px 48px',
        borderRadius: '12px',
        textTransform: 'none',
        fontSize: '14px',
        fontWeight: 700,
      }}
    >
      {t('gotIt')}
    </Button>
  );

  const handleOpenDiscardedVoiceCloningDialog = () => {
    dispatch(
      actions.dialog.displayDialogV2({
        notificationDialogV2: {
          name: 'voice-cloning-discard-info',
          title: t('unavailableVoice'),
          description: t('discardedVoiceCloning', {
            style: 'margin-left: -16px',
          }),
          open: true,
          actionComponent: renderButtonUnderstand(),
          showIcon: false,
          onClose: handleCloseNotificationDialogV2,
          variant: null,
        },
      }),
    );
  };

  const checkValidCreateSynthesis = (blockData) => {
    const validationResult = validateSynthesis({
      isMultiBlock: !blockData,
      onOpenOutOfCreditsDialog: () => setOpenOutOfCreditsDialog(true),
      onOpenExpiredPackageDialog: () => setOpenExpiredPackageDialog(true),
      onOpenDialogLockedCycle: setOpenDialogLockedCycle,
      onOpenMaxCharsLimitExceeded: () => setOpenMaxCharsLimitExceeded(true),
      onOpenLimitedFeatureDialog: openLimitedFeatureDialog,
      onSetWarningOneTimeCredits: setWarningOneTimeCredits,
      onOpenWrongSyntaxDialog: setOpenWrongSyntaxDialog,
      onOpenWrongSyntaxDialogV2: setOpenWrongSyntaxDialogV2,
      onOpenDiscardedVoiceCloning: handleOpenDiscardedVoiceCloningDialog,
      blockData,
    });

    return validationResult;
  };

  const renderMaxCharsLimitExceededDialog = () => {
    const isFreePackage = FREE_PACKAGE_CODES.includes(packageCode);

    if (isFreePackage) {
      return (
        <FreeMaxCharsLimitExceeded
          open={openMaxCharsLimitExceeded}
          onClose={handleCloseMaxCharsLimitExceededDialog}
          maxLengthInputText={maxLengthInputText}
        />
      );
    }

    return (
      <PaidMaxCharsLimitExceeded
        open={openMaxCharsLimitExceeded}
        onClose={handleCloseMaxCharsLimitExceededDialog}
        maxLengthInputText={maxLengthInputText}
      />
    );
  };

  const getSidebarWidth = () => {
    let sidebarWidth = 0;
    sidebarWidth = openSidebar ? 0 : SIDEBAR_WIDTH_COLLAPSED;

    return sidebarWidth;
  };
  const handleStopFocusOnBlock = () => {
    setFocusingBlockId(null);
  };

  const handleChangeCharacterExceed = (value) => {
    setCharacterExceed(value);
  };

  const handleFocusingBlock = (blockId) => {
    setFocusingBlockId(blockId);
    const audioLink = blocks[blockId]?.audioLink;
    if (!audioLink) return;
    dispatch(actions.project.setCurrentBlockId(blockId));
    handleLoadAudio(blockId);
  };

  const scrollToBlock = (blockId) => {
    setScrollToBlockId(blockId);
    // Clear after a short delay to allow the effect to trigger
    setTimeout(() => setScrollToBlockId(null), 200);
  };

  const getVoiceExist = async () => {
    const voiceExist = await apis.voices.getVoicesV2({
      active: true,
    });
    if (!voiceExist.status) return [];
    return voiceExist?.result?.voices;
  };
  const getVoiceDefault = async () => {
    const voiceExist = await getVoiceExist();
    if (voiceExist?.length) {
      dispatch(actions.project.setVoiceDefault(voiceExist[0]));
      dispatch(actions.project.setListVoice(voiceExist));
    }
  };

  const saveProject = async () => {
    const response = await updateProject(projectId, {
      title,
      blocks: blockIdsRef.current.map((blockId) => ({
        id: blockId,
        characters: countTextLength(blocksRef.current[blockId].text || ''),
        text: blocksRef.current[blockId].text || undefined,
        voiceCode: blocksRef.current[blockId]?.voiceCode || voiceDefault?.code,
        breakTime: blocksRef.current[blockId]?.breakTime || DEFAULT_BREAK_TIME,
        speed: blocksRef.current[blockId]?.speed || DEFAULT_SPEED,
        elements: blocksRef.current[blockId]?.elements,
        isSample: blocksRef.current[blockId]?.isSample || false,
      })),
      audioType,
    });
    return response;
  };

  useEffect(() => {
    blockIdsRef.current = blockIds;
  }, [blockIds]);

  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  useEffect(() => {
    getVoiceDefault();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      if (isDraft || isProjectConverting) {
        if (!isProjectConverting) {
          saveProject();
        }
        event.preventDefault();
        const e = event;
        e.returnValue = '';
        return undefined;
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDraft, t, isOpenUnsavedChangesDialog, blockIds, blocks]);

  const onLeavePage = () => {
    setIsOpenUnsavedChangesDialog(false);
    dispatch(actions.project.updateOriginalBlocks());
  };

  const handleCloseUnsavedChangesDialog = () => {
    onLeavePage();
  };

  const synthesizeMultiblock = async (synthesizeBlocks) => {
    const synthesizeBlockIds = synthesizeBlocks.map((block) => block.id);
    const synthesisPayload = { projectId, blocks: synthesizeBlocks };
    const data = await apis.synthesis.createSynthesisMultiBlock(
      synthesisPayload,
      {},
    );

    if (data.status) {
      const newBlocks = data.result.blocks.map((block) => ({
        id: block.id,
        requestId: block?.requestId,
        status: block.status,
      }));
      dispatch(
        actions.project.updateMultiBlockSynthesisInfo(
          synthesizeBlockIds,
          newBlocks,
        ),
      );
    } else {
      const { errorMessage, errorCode } = data;
      dispatch(
        actions.noti.push({
          severity: 'error',
          message:
            errorCode === ERROR_CODE.BAD_REQUEST
              ? 'errorOccurred'
              : 'synthesisFailure',
          value: errorMessage,
        }),
      );

      // Error converting the batch which will result in status of all blocks
      // of that batch is synthesis failure
      // Change all status of blocks to failure to display failure icon
      const failSynthesisBlocks = synthesizeBlocks.map((block) => ({
        ...block,
        status: CONVERTING_STATUS.FAILURE,
      }));
      dispatch(
        actions.project.updateMultiBlockSynthesisInfo(
          synthesizeBlockIds,
          failSynthesisBlocks,
        ),
      );
    }
  };

  const handleConvertAudio = async () => {
    const blockMaxCharsLimitExceeded = blockWithText.find(
      (block) => countTextLength(block?.text) > MAX_CHARACTER_PER_BLOCK,
    );
    if (blockMaxCharsLimitExceeded) {
      scrollToBlock(blockMaxCharsLimitExceeded?.id);
      return;
    }
    if (!checkValidCreateSynthesis()) return;
    const blockWithoutAudioLink = blockWithTextChanged
      ?.filter((block) => block.status !== CONVERTING_STATUS.IN_PROGRESS)
      ?.map((block) => ({
        text: block.text,
        voiceCode: block.voiceCode || voiceDefault?.code,
        speed: block.speed || DEFAULT_SPEED,
        audioType,
        id: block.id,
      }));
    const newBlockIds = blockWithoutAudioLink.map((block) => block.id);
    const mapBlockWithStatus = (status) =>
      blockWithoutAudioLink?.map((block) => ({
        id: block.id,
        status,
        audioExpiredAt: null, // Delete the old audioExpiredAt to remove the audio expired icon
      }));

    if (blockWithoutAudioLink.length) {
      dispatch(
        actions.project.updateMultiBlockSynthesisInfo(
          newBlockIds,
          mapBlockWithStatus(CONVERTING_STATUS.IN_PROGRESS),
        ),
      );
      dispatch(actions.project.setIsProjectSynthesizing(true));

      const response = await saveProject();
      if (!response.status) {
        dispatch(
          actions.project.updateMultiBlockSynthesisInfo(
            newBlockIds,
            mapBlockWithStatus(CONVERTING_STATUS.FAILURE),
          ),
        );
        return;
      }

      dispatch(actions.project.saveProject());

      for (let i = 0; i < blockWithoutAudioLink.length; i += 1) {
        const currentBlock = blockWithoutAudioLink[i];
        setQueueMultiBlock((prev) => [...prev, currentBlock]);
      }
    }
  };

  const handleConfirmLeave = async (callback) => {
    if (isProjectConverting) {
      onLeavePage();
      if (callback) callback();
      return;
    }
    const response = await saveProject();
    if (response?.status) {
      if (isOpenUnsavedChangesDialog) {
        window.location.reload();
      }
      onLeavePage();
      if (callback) callback();
    }
  };

  const renderOutOfCreditsDialog = () => {
    const isPackageStandardV3 =
      usingPackage.level === PACKAGE_LEVEL_V3.STANDARD &&
      usingPackage?.version?.includes(PACKAGE_STUDIO_VERSION.V3);

    const isPackageSpecialV3 =
      usingPackage.level === PACKAGE_LEVEL_V3.SPECIAL &&
      usingPackage?.version?.includes(PACKAGE_STUDIO_VERSION.V3);

    const isPackageVipV3 =
      usingPackage.level === PACKAGE_LEVEL_V3.VIP &&
      usingPackage?.version?.includes(PACKAGE_STUDIO_VERSION.V3);

    const isCustomPackage = checkCustomPackage({
      type: usingPackage?.type,
      level: usingPackage.level,
    });

    if (isCustomPackage) {
      return (
        <OutOfCreditsCustom
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          packageCode={packageCode}
        />
      );
    }

    if (isPackageStandardV3) {
      return (
        <OutOfCreditsStandard
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          packageCode={packageCode}
          // voice={voice}
        />
      );
    }

    if (isPackageSpecialV3) {
      return (
        <OutOfCreditsSpecial
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          packageCode={packageCode}
          // voice={voice}
        />
      );
    }

    if (isPackageVipV3)
      return (
        <OutOfCreditsVip
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          warningOneTimeCredits={warningOneTimeCredits}
        />
      );

    // Display this popup when using a free package or a package that is neither standard nor special v3.
    return (
      <OutOfCreditsFree
        open={openOutOfCreditsDialog}
        onClose={handleCloseOutOfCreditsDialog}
      />
    );
  };

  const renderUnsavedChangesActions = () => (
    <StyledActionWarning>
      <Button
        className="btn-non-vbee-warning"
        variant="outlined"
        onClick={handleCloseUnsavedChangesDialog}
      >
        {t('stayOnPage')}
      </Button>
      <Button
        className="btn-non-vbee-warning"
        variant="contained"
        onClick={handleConfirmLeave}
      >
        {t('leaveAnyway')}
      </Button>
    </StyledActionWarning>
  );

  const handleAddNewBlock = (
    blockId,
    blockData,
    isScrollToNewBlock = false,
  ) => {
    let newBlock = blockData;

    if (!blockData) {
      const newBlockId = uuidv4();
      const block = blocks[blockId];
      newBlock = [
        {
          voice: block?.voice,
          voiceCode: block?.voiceCode,
          speed: block?.speed,
          breakTime: block?.breakTime,
          elements: block?.elements,
          text: '',
          id: newBlockId,
        },
      ];

      setTimeout(() => {
        setFocusingBlockId(newBlockId);
      }, 0);

      if (!blockData && isScrollToNewBlock) {
        setTimeout(() => {
          addBlockRef.current?.[blockId]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 100);
      }
    }

    dispatch(actions.project.addBlock(blockId, newBlock));
  };

  const handleConfirmDeleteSentence = (blockId) => {
    if (currentBlockId === blockId) {
      const blockWithAudio = getBlockWithAudio(blockIds, blocks);
      const index = blockWithAudio.findIndex((block) => block.id === blockId);
      const nextBlockId = blockWithAudio[index + 1]?.id;
      dispatch(actions.project.setCurrentBlockId(nextBlockId));
    }
    dispatch(actions.project.deleteBlock(blockId));
    dispatch(actions.project.setIsPlaying(false));
  };

  const handleReorder = (list, blockId, direction) => {
    const result = [...list];
    const currentIndex = result.indexOf(blockId);
    if (direction === DIRECTION.UP) {
      const [removed] = result.splice(currentIndex, 1);
      result.splice(currentIndex - 1, 0, removed);
    } else {
      const [removed] = result.splice(currentIndex, 1);
      result.splice(currentIndex + 1, 0, removed);
    }
    return result;
  };

  const handleDragEnd = (blockId, direction) => {
    if (!direction) {
      return;
    }

    const newBlockIds = handleReorder(blockIdsRef.current, blockId, direction);
    dispatch(actions.project.updateOrderBlock(newBlockIds));
  };
  const checkRouteChange = (nextLocation) => {
    const arrayPath = nextLocation.pathname.split('/');
    return (
      ((arrayPath[1] === 'projects' && !arrayPath[2]) ||
        arrayPath[1] !== 'projects') &&
      (isDraft || isProjectConverting)
    );
  };
  const handleConfirmNonVbeeDialog = async () => {
    setOpenWrongSyntaxDialogV2(null);
  };
  const handleClickCharsFactorCheckbox = () => {
    if (!charsFactorCheckbox) {
      localStorage.setItem('nonVbeeDialog', true);
      localStorage.setItem('charsFactorDialog', true);
    } else {
      localStorage.removeItem('nonVbeeDialog');
      localStorage.removeItem('charsFactorDialog');
    }
    setCharsFactorCheckbox((value) => !value);
  };

  const handleClickBuyPackages = () => {
    history.push(ROUTES.PAYMENT);
  };
  const handleUpdateCredits = (idBlock, text, creditFactor) => {
    const credits = countTextLength(text) * (creditFactor || 1);
    dispatch(actions.project.updateCreditsBlocks(idBlock, credits));
  };

  const handleConvertBlockToAudio = async (block) => {
    // Save project data
    const response = await saveProject();

    if (!response.status) {
      dispatch(
        actions.project.updateBlockSynthesisInfo(block.id, {
          status: CONVERTING_STATUS.FAILURE,
        }),
      );
      return;
    }

    dispatch(actions.project.updateOriginalBlocks(block.id));
    // Create data for a synthesis request
    const synthesisPayload = {
      text: block.text,
      voiceCode: block?.voiceCode || voiceDefault?.code,
      speed: block?.speed || DEFAULT_SPEED,
      projectId,
      blockId: block.id,
      audioType,
    };

    // Call API to synthesize
    const data = await apis.synthesis.createSynthesis(synthesisPayload, {});

    // Update block synthesis data
    const isCreatedReqSuccess = data?.status;
    dispatch(
      actions.project.updateBlockSynthesisInfo(block.id, {
        ...(isCreatedReqSuccess
          ? { requestId: data.result?.requestId }
          : { status: CONVERTING_STATUS.FAILURE }),
      }),
    );
    dispatch(actions.project.deleteCreditsBlocks(block.id));

    if (!isCreatedReqSuccess) {
      const { errorMessage, errorCode } = data;
      switch (errorCode) {
        case ERROR_CODE.INVALID_SYNTAX: {
          setOpenWrongSyntaxDialog({
            name: 'error-syntax',
            variant: 'warning',
            title: t('errorSyntax'),
            description: t('errorSyntaxDescription'),
          });
          break;
        }

        case ERROR_CODE.SPAM_EMAIL: {
          setOpenWrongSyntaxDialogV2({
            name: 'error-syntax',
            variant: 'warning',
            title: t('cannotConvertTTS'),
            description: t('blockSpamEmailDescV2'),
          });
          break;
        }

        case ERROR_CODE.TEXT_TOO_LONG: {
          setOpenOutOfCreditsDialog(true);
          break;
        }

        case ERROR_CODE.INACTIVE_VOICE: {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message: t('inactiveVoiceNoti', {
                voiceName: block?.voice?.name,
              }),
              value: errorMessage,
            }),
          );
          break;
        }

        case ERROR_CODE.NO_MORE_SUPPORT_VOICE: {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message: t('inactiveVoiceNoti', {
                voiceName: block?.voice?.name,
              }),
              value: errorMessage,
            }),
          );
          break;
        }

        case ERROR_CODE.DISCARDED_VOICE_CLONING: {
          handleOpenDiscardedVoiceCloningDialog();
          break;
        }

        default: {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message:
                errorCode === ERROR_CODE.BAD_REQUEST
                  ? 'errorOccurred'
                  : 'synthesisFailure',
              value: errorMessage,
            }),
          );
        }
      }
    }
  };

  const processQueueMultiBlock = async () => {
    if (queueMultiBlock.length === 0 || isSynthesisMultiBlock) return;
    setIsSynthesisMultiBlock(true);
    const task = queueMultiBlock[0];

    try {
      await synthesizeMultiblock([task]);
      setQueueMultiBlock((prev) => prev.slice(1));
    } catch (error) {
      setQueueMultiBlock((prev) => prev.slice(1));
    } finally {
      setIsSynthesisMultiBlock(false);
    }
  };

  const processQueueSynthesis = async () => {
    if (queueSynthesis.length === 0 || isProcessing) return;

    setIsProcessing(true);
    const task = queueSynthesis[0];

    try {
      await handleConvertBlockToAudio(task);
      setQueueSynthesis((prev) => prev.slice(1));
    } catch (error) {
      setQueueSynthesis((prev) => prev.slice(1));
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    processQueueSynthesis();
  }, [queueSynthesis, isProcessing]);

  useEffect(() => {
    processQueueMultiBlock();
  }, [queueMultiBlock, isSynthesisMultiBlock]);

  const handleAddBlockToQueue = (block) => {
    if (!checkValidCreateSynthesis(block)) return;
    handleUpdateCredits(block.id, block.text, block.voice?.creditFactor);
    dispatch(
      actions.project.updateBlockSynthesisInfo(block.id, {
        status: CONVERTING_STATUS.IN_PROGRESS,
      }),
    );

    dispatch(actions.project.setIsProjectSynthesizing(false));
    setQueueSynthesis((prev) => [...prev, block]);
  };

  const renderCloseButtonV2 = (handleClose) => {
    switch (openWrongSyntaxDialogV2?.type) {
      case DIALOG_V2_TYPE.NON_VBEE_WARNING:
        return (
          <div className="non-vbee-warning">
            <FormControlLabel
              control={
                <Checkbox
                  classes={{ root: 'checkbox' }}
                  color="iconPrimary"
                  value={charsFactorCheckbox}
                  onChange={handleClickCharsFactorCheckbox}
                />
              }
              label={
                <Typography className="not-show-again-text">
                  {t('notShowAgain')}
                </Typography>
              }
              labelPlacement={t('notShowAgain')}
            />
            <div className="">
              <Button
                className="btn-non-vbee-warning"
                variant="outlined"
                onClick={handleClose}
              >
                {t('cancel')}
              </Button>
              <Button
                className="btn-non-vbee-warning"
                variant="contained"
                onClick={handleConfirmNonVbeeDialog}
              >
                {t('continueGenerate')}
              </Button>
            </div>
          </div>
        );
      case DIALOG_V2_TYPE.CANNOT_USE_VOICE:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={() => {
              setOpenWrongSyntaxDialogV2(null);
            }}
          >
            {t('gotItV2')}
          </Button>
        );
      case DIALOG_V2_TYPE.RENAME_VOICE:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={() => {
              setOpenWrongSyntaxDialogV2(null);
            }}
          >
            {t('gotIt')}
          </Button>
        );
      default:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={handleClickBuyPackages}
          >
            {t('buyPackages')}
          </Button>
        );
    }
  };

  const renderLockedCycleButton = () => (
    <Button variant="contained" onClick={() => setOpenDialogLockedCycle(null)}>
      {t('understoodV3')}
    </Button>
  );
  const renderCloseButton = (handleClose) => (
    <Button variant="contained" onClick={handleClose}>
      {t('understood')}
    </Button>
  );

  const renderChangeFormatButton = () => (
    <div className="change-format-button">
      <Button
        className="btn btn-cancel"
        variant="outlined"
        onClick={() =>
          dispatch(actions.project.setOpenDialogChangeFormat(false))
        }
      >
        {t('cancel')}
      </Button>
      <Button
        className="btn btn-agree"
        variant="contained"
        onClick={() => {
          dispatch(
            actions.project.setAudioType(audioType === 'mp3' ? 'wav' : 'mp3'),
          );
          dispatch(actions.project.setOpenDialogChangeFormat(false));
        }}
      >
        {t('agree')}
      </Button>
    </div>
  );

  return (
    <>
      <StyledContent
        sidebarWidth={getSidebarWidth()}
        isShowHeaderBanner={isShowHeaderBanner}
        onClick={handleStopFocusOnBlock}
        role="button"
        tabIndex="-1"
      >
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
          role="button"
          tabIndex="-1"
          style={{
            height: `calc(100vh - ${HEADER_HEIGHT}px - ${AUDIO_PLAYER_HEIGHT}px - ${
              isShowHeaderBanner ? FLASH_SALE_BANNER_HEIGHT : 0
            }px)`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <VirtualizedContentList
            blockIds={blockIds}
            blocks={blocks}
            originalBlocks={originalBlocks}
            containerHeight="100%"
            renderContentBlockProps={{
              audioType,
              characterExceed,
              onChangeCharacterExceed: handleChangeCharacterExceed,
              onAddNewParagraphs: handleAddNewBlock,
              onSelectParagraphId: handleConfirmDeleteSentence,
              onOpenDiscardedVoiceCloningDialog:
                handleOpenDiscardedVoiceCloningDialog,
              focusingBlockId,
              onFocusingBlock: handleFocusingBlock,
              onDragEnd: handleDragEnd,
              onSaveProject: saveProject,
              voiceDefault,
              isDownloading,
              onOpenOutOfCreditsDialog: setOpenOutOfCreditsDialog,
              checkValidCreateSynthesis,
              currentBlockId,
              handlePlayAudio: handlePlayAudioInBlock,
              onOpenWrongSyntaxDialog: setOpenWrongSyntaxDialog,
              onOpenWrongSyntaxDialogV2: setOpenWrongSyntaxDialogV2,
              projectId,
              isPlayingBlock,
              currentPlayingBlockId,
              onConvertBlockToAudio: handleAddBlockToQueue,
              scrollToBlockId,
              isPlaying,
            }}
            showAddButton
            addButtonProps={{
              onClick: () =>
                handleAddNewBlock(blockIds[blockIds.length - 1], null, true),
              text: t('addMoreBlock'),
              icon: addBlockIcon,
            }}
          />
        </div>
      </StyledContent>

      <AudioPlayer
        isPlayAudio={
          isAudioLinkExist &&
          !!blockWithText.length &&
          !blockWithTextChanged?.length &&
          !hasAudioExpired
        }
        handleConvertAudio={handleConvertAudio}
        isConverting={isProjectConverting}
        isDisableConvertBtn={isDisableConvertBtn}
        isLoading={isLoading}
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        handlePlay={handlePlay}
        handlePause={handlePause}
        handleSeeked={handleSeeked}
        title={title}
        blocks={blocks}
        handleChangeVolume={handleChangeVolume}
        onSaveProject={saveProject}
        synthesisProgress={synthesisProgress}
        isDisabledBtnAudioEffects={isDisabledBtnAudioEffects}
      />

      <NotificationDialogV3
        name="unsaved-changes"
        open={isOpenUnsavedChangesDialog}
        variant="none"
        title={t('unsavedChangesTitle')}
        padding="12px 24px"
        paddingHeader="8px 24px"
        description={t('unsavedChangesDescription')}
        onClose={() => setIsOpenUnsavedChangesDialog(false)}
        actionComponent={renderUnsavedChangesActions()}
      />
      <CustomActionLeavePage
        when={isDraft}
        navigate={(path) => {
          history.push(path);
        }}
        shouldBlockNavigation={checkRouteChange}
        visiblePopup={isOpenUnsavedChangesDialog}
        onLeavePage={handleConfirmLeave}
        onCancel={handleCloseUnsavedChangesDialog}
      />
      <CustomActionLeavePage
        isProjectConverting
        when={isProjectConverting}
        navigate={(path) => {
          history.push(path);
        }}
        shouldBlockNavigation={checkRouteChange}
        visiblePopup={isOpenUnsavedChangesDialog}
        onLeavePage={handleConfirmLeave}
        onCancel={handleCloseUnsavedChangesDialog}
      />
      <NotificationDialogV3
        name={openWrongSyntaxDialogV2?.name}
        title={openWrongSyntaxDialogV2?.title}
        description={openWrongSyntaxDialogV2?.description}
        subDescription={openWrongSyntaxDialogV2?.subDescription}
        variant={openWrongSyntaxDialogV2?.variant}
        open={!!openWrongSyntaxDialogV2?.title}
        onClose={() => setOpenWrongSyntaxDialogV2(null)}
        actionComponent={renderCloseButtonV2(() =>
          setOpenWrongSyntaxDialogV2(null),
        )}
      />
      {renderOutOfCreditsDialog()}
      {renderMaxCharsLimitExceededDialog()}

      {!!openDialogLockedCycle?.title && (
        <NotificationDialogV4
          image={LockCycle}
          title={openDialogLockedCycle?.title}
          description={openDialogLockedCycle?.description}
          actionComponent={renderLockedCycleButton()}
          open={!!openDialogLockedCycle?.title}
          onClose={() => setOpenDialogLockedCycle(null)}
        />
      )}

      <NotificationDialogV3
        name="download-format"
        title={t('noteV2')}
        description={t('desscriptionChangeFormat')}
        variant="warning"
        open={openDialogChangeFormat}
        onClose={() =>
          dispatch(actions.project.setOpenDialogChangeFormat(false))
        }
        actionComponent={renderChangeFormatButton()}
        padding="12px 32px"
      />

      <NotificationDialog
        name={openWrongSyntaxDialog?.name}
        title={openWrongSyntaxDialog?.title}
        description={openWrongSyntaxDialog?.description}
        subDescription={openWrongSyntaxDialog?.subDescription}
        variant={openWrongSyntaxDialog?.variant}
        open={!!openWrongSyntaxDialog?.title}
        onClose={() => setOpenWrongSyntaxDialog(null)}
        actionComponent={renderCloseButton(() =>
          setOpenWrongSyntaxDialog(null),
        )}
      />
      <ExpiredPackageAlert
        open={openExpiredPackageDialog}
        onClose={handleCloseExpiredPackageDialog}
      />
    </>
  );
};

export default Content;
